// src/services/restaurant/restaurantFactory.ts

import { 
    collection, 
    doc, 
    setDoc, 
    serverTimestamp,
    writeBatch,
    getDoc
  } from 'firebase/firestore';
  import { db } from '../../config/firebase/config';
  import {
    Restaurant,
    ProcessorEnvironment,
    StripeConfig,
    CardNetConfig,
    AzulConfig,
    DEFAULT_RESTAURANT_SETTINGS,
    PaymentProcessor
  } from '../../types/restaurant';
  import { POSConfig } from '../pos/types';
  
/**
 * INPUT for creating a new restaurant
 */
export interface CreateRestaurantInput {
  name: string;
  processor: PaymentProcessor;  // 'stripe' | 'cardnet' | 'azul' | 'none'
  
  // ========== POS INTEGRATION (SUPER ADMIN ONLY) ==========
  integrationMode: 'api' | 'webhook' | 'polling' | 'none';  // CRITICAL: Set by super admin
  
  // Processor-specific configs (provide only the one you're using)
  stripeConfig?: Partial<StripeConfig>;
  cardnetConfig?: Partial<CardNetConfig>;
  azulConfig?: Partial<AzulConfig>;
  
  // Optional overrides for settings
  settings?: Partial<typeof DEFAULT_RESTAURANT_SETTINGS>;
  active?: boolean;
  
  // Optional contact info
  address?: string;
  phone?: string;
  email?: string;
  logoUrl?: string;
}
  
/**
 * DEFAULT POS CONFIG
 * NOTE: integrationMode is now set at Restaurant level, not here
 * This config only contains provider, credentials, and sync settings
 */
const DEFAULT_POS_CONFIG: POSConfig = {
  provider: 'mock',
  enabled: true,
  credentials: {},  
  syncSettings: {
    autoSync: true,
    syncIntervalMinutes: 5
  }
};
  
  /**
   * Restaurant Factory Service
   * Handles automated, validated restaurant creation
   */
  export class RestaurantFactory {
    
    /**
     * Create a new restaurant with complete, validated structure
     */
    static async createRestaurant(input: CreateRestaurantInput): Promise<string> {
      // Step 1: Build complete restaurant object with defaults
      const restaurantData = this.buildRestaurantData(input);
      
      // Step 2: Validate the data
      const errors = this.validateRestaurant(restaurantData);
      if (errors.length > 0) {
        throw new Error(`Restaurant validation failed: ${errors.join(', ')}`);
      }
      
      // Step 3: Create Firestore document
      const restaurantRef = doc(collection(db, 'restaurants'));
      const restaurantId = restaurantRef.id;
      
      // Step 4: Remove undefined values before saving (Firestore doesn't allow undefined)
      const cleanData = JSON.parse(JSON.stringify(restaurantData));
      
      // Step 5: Save to Firestore
      await setDoc(restaurantRef, {
        ...cleanData,
        id: restaurantId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      
      console.log(`✅ Restaurant created successfully: ${restaurantId}`);
      return restaurantId;
    }
    
  /**
   * Build complete restaurant data with defaults
   */
  private static buildRestaurantData(input: CreateRestaurantInput): Omit<Restaurant, 'id' | 'createdAt' | 'updatedAt'> {
    const base: any = {
      name: input.name.trim(),
      active: input.active ?? true,
      processor: input.processor,
      // ========== POS INTEGRATION ==========
      integrationMode: input.integrationMode,  // CRITICAL: Set by super admin, inherited by mesas
      posConfig: DEFAULT_POS_CONFIG,
      settings: {
        ...DEFAULT_RESTAURANT_SETTINGS,
        ...input.settings,
      },
    };
      
      // Add optional contact info
      if (input.address) base.address = input.address;
      if (input.phone) base.phone = input.phone;
      if (input.email) base.email = input.email;
      if (input.logoUrl) base.logoUrl = input.logoUrl;
      
      // Add processor-specific configuration
      switch (input.processor) {
        case 'stripe':
          if (!input.stripeConfig) {
            throw new Error('Stripe configuration is required when processor is stripe');
          }
          base.stripeConfig = this.buildStripeConfig(input.stripeConfig);
          break;
          
        case 'cardnet':
          if (!input.cardnetConfig) {
            throw new Error('CardNet configuration is required when processor is cardnet');
          }
          base.cardnetConfig = this.buildCardNetConfig(input.cardnetConfig);
          break;
          
        case 'azul':
          if (!input.azulConfig) {
            throw new Error('Azul configuration is required when processor is azul');
          }
          base.azulConfig = this.buildAzulConfig(input.azulConfig);
          break;
          
        case 'none':
          // No processor config needed
          break;
          
        default:
          throw new Error(`Unknown processor type: ${input.processor}`);
      }
      
      return base;
    }
    
    /**
     * Build complete Stripe config with defaults
     */
    private static buildStripeConfig(partial: Partial<StripeConfig>): StripeConfig {
      if (!partial.publicKey) {
        throw new Error('Stripe publicKey is required');
      }
      
      // Auto-detect environment from key prefix
      const environment: ProcessorEnvironment = partial.environment || 
        (partial.publicKey.startsWith('pk_test_') ? 'test' : 'live');
      
      return {
        enabled: partial.enabled ?? true,
        environment,
        publicKey: partial.publicKey,
        merchantId: partial.merchantId || '',
        secretName: partial.secretName
      };
    }
    
    /**
     * Build complete CardNet config with defaults
     */
    private static buildCardNetConfig(partial: Partial<CardNetConfig>): CardNetConfig {
      if (!partial.merchantId) {
        throw new Error('CardNet merchantId is required');
      }
      
      return {
        enabled: partial.enabled ?? true,
        environment: partial.environment || 'sandbox',
        merchantId: partial.merchantId,
        secretName: partial.secretName,
        apiSecret: partial.apiSecret
      };
    }
    
    /**
     * Build complete Azul config with defaults
     */
    private static buildAzulConfig(partial: Partial<AzulConfig>): AzulConfig {
      // Check for GSM secret name OR legacy credentials
      const hasCredentials = partial.secretName || (partial.auth1 && partial.auth2);
      
      if (!hasCredentials) {
        throw new Error('Azul requires either secretName (GSM) or auth1/auth2 credentials');
      }
      
      if (!partial.merchantId) {
        throw new Error('Azul merchantId is required');
      }
      
      return {
        enabled: partial.enabled ?? true,
        environment: partial.environment || 'sandbox',
        auth1: partial.auth1,  // Optional - only for legacy
        auth2: partial.auth2,  // Optional - only for legacy
        channel: partial.channel || 'EC',
        merchantId: partial.merchantId,
        secretName: partial.secretName  // Add GSM secret name
      };
    }
    
    /**
     * Validate restaurant data
     */
    private static validateRestaurant(data: Partial<Restaurant>): string[] {
      const errors: string[] = [];
      
      if (!data.name || data.name.trim().length === 0) {
        errors.push('Restaurant name is required');
      }
      
      if (!data.processor) {
        errors.push('Processor type is required');
      }
      
      // Validate processor config exists when needed
      if (data.processor === 'stripe' && !data.stripeConfig) {
        errors.push('Stripe configuration is required when processor is stripe');
      }
      if (data.processor === 'cardnet' && !data.cardnetConfig) {
        errors.push('CardNet configuration is required when processor is cardnet');
      }
      if (data.processor === 'azul' && !data.azulConfig) {
        errors.push('Azul configuration is required when processor is azul');
      }
      
      return errors;
    }
    
    /**
     * Update existing restaurant (validates changes)
     */
    static async updateRestaurant(
      restaurantId: string,
      updates: Partial<Restaurant>
    ): Promise<void> {
      const restaurantRef = doc(db, 'restaurants', restaurantId);
      
      // Get current data
      const snapshot = await getDoc(restaurantRef);
      if (!snapshot.exists()) {
        throw new Error(`Restaurant ${restaurantId} not found`);
      }
      
      const currentData = snapshot.data() as Restaurant;
      const updatedData = { ...currentData, ...updates };
      
      // Validate merged data
      const errors = this.validateRestaurant(updatedData);
      if (errors.length > 0) {
        throw new Error(`Update validation failed: ${errors.join(', ')}`);
      }
      
      // Update with timestamp
      await setDoc(restaurantRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      
      console.log(`✅ Restaurant updated: ${restaurantId}`);
    }
  }